/**
 * SalespeopleChips — visual treatment for a peer set of salespeople.
 *
 * Renders first-2 members as `Chip` atoms + a `+N` overflow chip when the
 * set exceeds 2. This IS the treatment operator sees today on the golden
 * customer card as `lvalley · Adrian · +2`.
 *
 * **Peer semantic, no designated member (LEN-UX-01 U-17 exemption).**
 *
 * Salespeople are the one relation `U-17` explicitly carves out from the
 * "designated member first, never arbitrary first-in-array" rule. Authored
 * verbatim at `Iterations/Iteration_18/Customer_Card_Options/APP-02-customer-info-card-COMPONENT.html:531`:
 *
 *   > Salespeople (State 4): no designated primary — salespeople are peers
 *   > in CU-1 — so no member is marked and the order is as-assigned. This
 *   > is the one relation with no 'designated member', and the sheet must
 *   > not invent one.
 *
 * Mirrored across every canonical copy of `U-17`. Backend confirms — no
 * `primary_salesperson_id` / `is_primary` / `designated` field exists on
 * Customer, Quote, Job, Invoice, or WorkOrder. First-2 in array is
 * assigned-order, not precedence.
 *
 * **Do NOT `.sort()` the incoming array.**
 *
 * Extracted from `GoldenCustomerCard.js:407-419` (PT-53b F8, 2026-08-03)
 * for reuse across landing tables + customer detail Quotes tab. Cited in
 * `COMPONENT_MAP` per `SD-6`. Consumers pass the plural set via
 * `normaliseSalespeople(record)` which handles the six fallback shapes
 * (salespeople / salespersons / singular legacy / etc.) — see
 * `utils/normaliseSalespeople.js`.
 *
 * Props:
 *   salespeople   Array<{ id?, name }>    peer set; order = as-assigned
 *   interactive?  boolean                 default true. In preview variants
 *                                         (e.g. table cells) set false —
 *                                         `data-portica-interactive` reflects
 *                                         it for tests / potential styling
 *                                         hooks. Chip itself has no click
 *                                         handler by default; the visual is
 *                                         the same.
 */
import React from 'react';
import { Chip } from './Chip';

export function SalespeopleChips({ salespeople, interactive = true, color = 'primary', swatch, className, style }) {
  const shown = (salespeople || []).slice(0, 2);
  const more = (salespeople || []).length - shown.length;
  const cls = ['flex items-center gap-1.5 flex-wrap', className].filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      style={style}
      data-portica-interactive={interactive ? 'true' : 'false'}
    >
      {shown.map((sp, i) => (
        <Chip key={sp.id || i} color={color}>
          {swatch && <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: swatch }} />}
          {sp.name}
        </Chip>
      ))}
      {more > 0 && <Chip color={color}>+{more}</Chip>}
    </div>
  );
}

export default SalespeopleChips;
